import { Optional } from '@/generic/functional';
import type { Categories } from '@/rule/domain/Categories.ts';
import type { Category } from '@/rule/domain/Category.ts';
import { Examples } from '@/rule/domain/Examples.ts';
import { OrganizationId } from '@/rule/domain/OrganizationId.ts';
import { RuleId } from '@/rule/domain/RuleId.ts';
import type { RuleStatus } from '@/rule/domain/RuleStatus.ts';

export class Title {
  readonly #value: string;

  private constructor(value: string) {
    this.#value = value;
  }

  get value(): string {
    return this.#value;
  }

  static of(title: Title | string) {
    if (title instanceof Title) {
      return title;
    }
    return new Title(title);
  }
}

export class Description {
  readonly #value: string;

  private constructor(value: string) {
    this.#value = value;
  }

  get value(): string {
    return this.#value;
  }

  static of(description: Description | string) {
    if (description instanceof Description) {
      return description;
    }
    return new Description(description);
  }
}

const CRITICAL = Symbol('Critical');

const HIGH = Symbol('High');
const MEDIUM = Symbol('Medium');
const LOW = Symbol('Low');
const INFO = Symbol('Info');
export const Severity = { CRITICAL, HIGH, MEDIUM, LOW, INFO } as const;

export type Severity = (typeof Severity)[keyof typeof Severity];

type RuleBuilder = {
  id: RuleId | string;
  title: Title | string;
  description?: Optional<Description | string>;
  severity: Severity;
  categories: Categories;
  status: RuleStatus;
  examples?: Examples;
  organizationId?: Optional<OrganizationId | string>;
};

export class Rule {
  readonly #id: RuleId;
  readonly #title: Title;
  readonly #description: Optional<Description>;
  readonly #severity: Severity;
  readonly #categories: Categories;
  readonly #status: RuleStatus;
  readonly #examples: Examples;
  readonly #organizationId: Optional<OrganizationId>;

  private constructor(builder: RuleBuilder) {
    this.#id = RuleId.of(builder.id);
    this.#title = Title.of(builder.title);
    this.#description = Optional.ofFalsifiable(builder.description).flatMap((optionalDescription) =>
      optionalDescription.map((description) => Description.of(description)),
    );
    this.#severity = builder.severity;
    this.#categories = builder.categories;
    this.#status = builder.status;
    this.#examples = Optional.ofFalsifiable(builder.examples).orElseGet(() => Examples.of());
    this.#organizationId = Optional.ofFalsifiable(builder.organizationId).flatMap(
      (optionalOrganizationId) =>
        optionalOrganizationId.map((organizationId) => OrganizationId.of(organizationId)),
    );
  }

  static of(builder: RuleBuilder): Rule {
    return new Rule(builder);
  }

  hasCategory(category: Category) {
    return this.#categories.has(category);
  }

  get hasNoCategory(): boolean {
    return this.#categories.isEmpty();
  }

  get id(): RuleId {
    return this.#id;
  }

  get title(): Title {
    return this.#title;
  }

  get description(): Optional<Description> {
    return this.#description;
  }

  get severity(): Severity {
    return this.#severity;
  }

  get categories(): Categories {
    return this.#categories;
  }

  get status(): RuleStatus {
    return this.#status;
  }

  get examples(): Examples {
    return this.#examples;
  }

  get organizationId(): Optional<OrganizationId> {
    return this.#organizationId;
  }
}
